import React, { useState, useEffect, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { getCustomerPurchaseOrders, createCustomerPurchaseOrder, updateCustomerPurchaseOrder } from "../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import { isMSP } from "../../helpers/util_helper";
import CustomerPurchaseOrdersHeader from "./CustomerPurchaseOrdersHeader";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  try {
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

const emptyCreateForm = () => ({
  po_number: "",
  order_number: "",
  terms: "",
  ship_via: "",
  order_date: "",
  is_default: false,
});

const emptyEditForm = () => ({
  is_default: false,
});

const CustomerPurchaseOrders = () => {
  const history = useHistory();
  const { customerOrgCode } = useParams();
  const location = useLocation();
  const customerName = location.state?.customerName ?? "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPo, setEditingPo] = useState(null);
  const [createForm, setCreateForm] = useState(emptyCreateForm());
  const [editForm, setEditForm] = useState(emptyEditForm());
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const editInitialRef = useRef(null);

  const isMSPUser = isMSP();

  const fetchList = () => {
    if (!customerOrgCode) return;
    getCustomerPurchaseOrders(customerOrgCode)
      .then((res) => {
        const data = res?.data ?? res;
        setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => setItems([]));
  };

  useEffect(() => {
    if (!isMSPUser) {
      history.push("/dashboard");
      return;
    }
  }, [isMSPUser, history]);

  useEffect(() => {
    if (!customerOrgCode) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCustomerPurchaseOrders(customerOrgCode)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        const list = Array.isArray(data?.items) ? data.items : [];
        setItems(list);
      })
      .catch((err) => {
        if (cancelled) return;
        const data = err?.response?.data ?? err?.data;
        const msg = data != null ? getApiErrorMessage(data) : (err?.message || "Failed to load purchase orders.");
        setError(String(msg));
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [customerOrgCode]);

  const handleOpenCreate = () => {
    const noRecords = items.length === 0;
    setCreateForm(noRecords ? { ...emptyCreateForm(), is_default: true } : emptyCreateForm());
    setSubmitError(null);
    setCreateModalOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateModalOpen(false);
    setSubmitError(null);
  };

  const handleCreateSubmit = async () => {
    const po_number = (createForm.po_number ?? "").trim();
    const order_number = (createForm.order_number ?? "").trim();
    const terms = (createForm.terms ?? "").trim();
    const ship_via = (createForm.ship_via ?? "").trim();
    const order_date = (createForm.order_date ?? "").trim();
    if (!po_number) {
      setSubmitError("PO Number is required.");
      return;
    }
    if (!order_number) {
      setSubmitError("Order Number is required.");
      return;
    }
    if (!terms) {
      setSubmitError("Terms is required.");
      return;
    }
    if (!ship_via) {
      setSubmitError("Ship via is required.");
      return;
    }
    if (!order_date) {
      setSubmitError("Order date is required.");
      return;
    }
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      const payload = {
        po_number,
        order_number,
        terms,
        ship_via,
        order_date,
        is_default: Boolean(createForm.is_default),
      };
      const res = await createCustomerPurchaseOrder(customerOrgCode, payload);
      const data = res?.data ?? res;
      if (res?.status === 201 || data?.purchase_order) {
        setSnackbar({ open: true, message: data?.message ?? "Purchase order created successfully.", severity: "success" });
        handleCloseCreate();
        fetchList();
      } else {
        setSubmitError(getApiErrorMessage(data) || "Create failed.");
      }
    } catch (err) {
      const data = err?.response?.data ?? err?.data;
      setSubmitError(data != null ? getApiErrorMessage(data) : (err?.message || "Create failed."));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleOpenEdit = (po) => {
    setEditingPo(po);
    const initial = { is_default: Boolean(po?.is_default) };
    editInitialRef.current = initial;
    setEditForm(initial);
    setSubmitError(null);
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setEditingPo(null);
    editInitialRef.current = null;
    setSubmitError(null);
  };

  const createFormFilled =
    (createForm.po_number ?? "").trim() !== "" &&
    (createForm.order_number ?? "").trim() !== "" &&
    (createForm.terms ?? "").trim() !== "" &&
    (createForm.ship_via ?? "").trim() !== "" &&
    (createForm.order_date ?? "").trim() !== "";

  const initial = editInitialRef.current;
  const hasEditFormChanges =
    editModalOpen && editingPo && initial != null && Boolean(editForm.is_default) !== Boolean(initial.is_default);

  const handleEditSubmit = async () => {
    if (!editingPo?.id) return;
    const is_default = Boolean(editForm.is_default);
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      const payload = { is_default };
      const res = await updateCustomerPurchaseOrder(customerOrgCode, editingPo.id, payload);
      const data = res?.data ?? res;
      if (res?.status === 200 || data?.purchase_order) {
        setSnackbar({ open: true, message: data?.message ?? "Purchase order updated successfully.", severity: "success" });
        handleCloseEdit();
        fetchList();
      } else {
        setSubmitError(getApiErrorMessage(data) || "Update failed.");
      }
    } catch (err) {
      const data = err?.response?.data ?? err?.data;
      setSubmitError(data != null ? getApiErrorMessage(data) : (err?.message || "Update failed."));
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!customerOrgCode) {
    history.replace("/billing-management");
    return null;
  }

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mb: "1.5rem",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      <CustomerPurchaseOrdersHeader customerName={customerName} />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
        </Box>
      ) : error ? (
        <Card sx={{ p: 3, borderRadius: 2, border: "1px solid #e5e7eb" }}>
          <Typography color="error">{error}</Typography>
        </Card>
      ) : (
        <Card sx={{ borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "none", backgroundColor: "#ffffff", overflow: "hidden" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, borderBottom: "1px solid #e5e7eb", backgroundColor: "#fafafa" }}>
            <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>Purchase orders</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ textTransform: "none", fontWeight: 500, borderRadius: "6px", backgroundColor: "#3b82f6", "&:hover": { backgroundColor: "#2563eb" } }}
            >
              Create
            </Button>
          </Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow sx={{ "& th": { backgroundColor: "#f4f4f5", borderBottom: "2px solid #e4e4e7", py: 1.5, px: 2, fontWeight: 600, fontSize: "0.875rem", color: "#52525b" } }}>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Order Number</TableCell>
                  <TableCell>Terms</TableCell>
                  <TableCell>Ship via</TableCell>
                  <TableCell>Order date</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Created by</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Updated by</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: "#71717a", fontSize: "0.875rem" }}>
                      No purchase orders found
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((po) => (
                    <TableRow key={po.id} sx={{ "& td": { borderBottom: "1px solid rgba(0,0,0,0.06)", py: 1.5, px: 2, fontSize: "0.875rem" } }}>
                      <TableCell sx={{ fontWeight: 500, color: "#18181b" }}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                          <span>{po.po_number ?? "—"}</span>
                          {po.is_default === true && (
                            <Chip label="Default" size="small" sx={{ height: 18, fontSize: "0.6875rem", fontWeight: 600 }} color="primary" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: "#52525b" }}>{po.order_number ?? "—"}</TableCell>
                      <TableCell sx={{ color: "#52525b" }}>{po.terms ?? "—"}</TableCell>
                      <TableCell sx={{ color: "#52525b" }}>{po.ship_via ?? "—"}</TableCell>
                      <TableCell sx={{ color: "#52525b" }}>{formatDate(po.order_date)}</TableCell>
                      <TableCell sx={{ color: "#52525b" }}>{formatDateTime(po.created_at)}</TableCell>
                      <TableCell sx={{ color: "#52525b" }}>{po.created_by ?? po.createdBy ?? "—"}</TableCell>
                      <TableCell sx={{ color: "#52525b" }}>{formatDateTime(po.updated_at)}</TableCell>
                      <TableCell sx={{ color: "#52525b" }}>{po.updated_by ?? po.updatedBy ?? "—"}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEdit(po)} sx={{ color: "#71717a", "&:hover": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Create Purchase Order modal */}
      <Dialog open={createModalOpen} onClose={handleCloseCreate} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.125rem" }}>Create Purchase Order</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="PO Number"
              required
              value={createForm.po_number}
              onChange={(e) => setCreateForm((p) => ({ ...p, po_number: e.target.value }))}
              size="small"
              fullWidth
              placeholder="e.g. PO-123"
            />
            <TextField
              label="Order Number"
              required
              value={createForm.order_number}
              onChange={(e) => setCreateForm((p) => ({ ...p, order_number: e.target.value }))}
              size="small"
              fullWidth
              placeholder="e.g. 25175669"
            />
            <TextField label="Terms" required value={createForm.terms} onChange={(e) => setCreateForm((p) => ({ ...p, terms: e.target.value }))} size="small" fullWidth placeholder="e.g. NET 30" />
            <TextField label="Ship via" required value={createForm.ship_via} onChange={(e) => setCreateForm((p) => ({ ...p, ship_via: e.target.value }))} size="small" fullWidth placeholder="e.g. GROUND" />
            <TextField
              label="Order date"
              required
              type="date"
              value={createForm.order_date}
              onChange={(e) => setCreateForm((p) => ({ ...p, order_date: e.target.value }))}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={<Checkbox checked={createForm.is_default} onChange={(e) => setCreateForm((p) => ({ ...p, is_default: e.target.checked }))} />}
              label="Set as default PO"
            />
            {submitError && <Typography color="error" variant="caption">{submitError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={handleCloseCreate} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={submitLoading || !createFormFilled} sx={{ textTransform: "none" }}>
            {submitLoading ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Purchase Order modal */}
      <Dialog open={editModalOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.125rem" }}>Edit Purchase Order</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={editForm.is_default} onChange={(e) => setEditForm((p) => ({ ...p, is_default: e.target.checked }))} />}
              label="Set as default PO"
            />
            {submitError && <Typography color="error" variant="caption">{submitError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={handleCloseEdit} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={submitLoading || !hasEditFormChanges} sx={{ textTransform: "none" }}>
            {submitLoading ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerPurchaseOrders;
